import { useContext, useState } from "react";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import {
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
} from "@mui/material";
import { SnackBarContext } from "Context/SnackContext";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios, { AxiosError } from "axios";
import { InFlow } from "Features/services/inFlow.api";

interface IProps {
  closeModal: () => void;
  setCreatedProduct: any;
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Product Name is required"),
  sku: Yup.string().required("SKU is required"),
  standardUomName: Yup.string(),
  unitPrice: Yup.number().required("Unit Price is required").typeError("Unit Price must be a number"),
  itemType: Yup.string().required("Item Type is required"),
  totalQuantityAvailable: Yup.number(),
  totalQuantityOnHand: Yup.number().required("Total Quantity On Hand is required").typeError("Total Quantity On Hand must be a number"),
  weight: Yup.number(),
  width: Yup.number(),
});

function isAxiosError(error: any): error is AxiosError {
  return error.isAxiosError;
}

function hasMessage(error: any): error is { message: string } {
  return error && typeof error.message === "string";
}

const CreateProducts = ({ closeModal, setCreatedProduct }: IProps) => {
  
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const { handleSnackbar } = useContext(SnackBarContext);


  let payload: any ;

  const formik = useFormik({
    initialValues: {
      name: "",
      sku: "",
      standardUomName: "",
      totalQuantityAvailable: 0,
      totalQuantityOnHand: 0,
      trackSerials: false,
      unitPrice: 0,
      weight: 0,
      width: 0,
      isActive: false,
      itemType: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {

      payload = {
            name: values.name,
            sku: values.sku,
            standardUomName: values.standardUomName,
            totalQuantityAvailable: values.totalQuantityAvailable,
            totalQuantityOnHand: values.totalQuantityOnHand,
            itemType: values.itemType,
            unitPrice: values.unitPrice,
            weight: values.weight,
            width: values.width,
            trackSerials: values.trackSerials,
            isActive: values.isActive,
      };

      setIsLoading(true);
      setErrorMsg("");

      try {
        const response = await InFlow.instance.createProduct(payload);
        handleSnackbar(true, "Successfully Created", "success");
        setCreatedProduct(true)
        closeModal();
      } catch (error) {
        if (isAxiosError(error)) {
          setErrorMsg(error.message || "An error occurred");
          handleSnackbar(true, error?.message || "An error occurred");
        } else if (hasMessage(error)) {
          setErrorMsg(error.message);
          handleSnackbar(true, error.message, "error");
        } else {
          setErrorMsg("An unexpected error occurred");
          handleSnackbar(true, "An unexpected error occurred",);
        }
      } finally {
        setIsLoading(false);
      }
    },
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
  } = formik;

  return (
    <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 3 }}>
      
      <TextField
        fullWidth
        id="name"
        name="name"
        label="Product Name"
        value={values.name}
        onChange={handleChange}
        onBlur={handleBlur}
        error={touched.name && Boolean(errors.name)}
        helperText={touched.name && errors.name}
        required
        variant="outlined"
        sx={{ mb: 1 }}
      />

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            type="number"
            id="unitPrice"
            name="unitPrice"
            label="Unit Price"
            value={values.unitPrice}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.unitPrice && Boolean(errors.unitPrice)}
            helperText={touched.unitPrice && errors.unitPrice}
            required
            variant="outlined"
            sx={{ mb: 1 }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            id="sku"
            name="sku"
            label="SKU"
            value={values.sku}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.sku && Boolean(errors.sku)}
            helperText={touched.sku && errors.sku}
            required
            variant="outlined"
            sx={{ mb: 1 }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            id="standardUomName"
            name="standardUomName"
            label="Standard UOM Name"
            value={values.standardUomName}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.standardUomName && Boolean(errors.standardUomName)}
            helperText={touched.standardUomName && errors.standardUomName}
            required
            variant="outlined"
            sx={{ mb: 1 }}
          />
        </Grid>

        <Grid item xs={3}>
          <TextField
            fullWidth
            type="number"
            id="weight"
            name="weight"
            label="Weight"
            value={values.weight}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.weight && Boolean(errors.weight)}
            helperText={touched.weight && errors.weight}
            variant="outlined"
            sx={{ mb: 1 }}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            fullWidth
            type="number"
            id="width"
            name="width"
            label="Width"
            value={values.width}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.width && Boolean(errors.width)}
            helperText={touched.width && errors.width}
            variant="outlined"
            sx={{ mb: 1 }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            type="number"
            id="totalQuantityAvailable"
            name="totalQuantityAvailable"
            label="Total Quantity Available"
            value={values.totalQuantityAvailable}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.totalQuantityAvailable && Boolean(errors.totalQuantityAvailable)}
            helperText={touched.totalQuantityAvailable && errors.totalQuantityAvailable}
            variant="outlined"
            sx={{ mb: 1 }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            type="number"
            id="totalQuantityOnHand"
            name="totalQuantityOnHand"
            label="Total Quantity On Hand"
            value={values.totalQuantityOnHand}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.totalQuantityOnHand && Boolean(errors.totalQuantityOnHand)}
            helperText={touched.totalQuantityOnHand && errors.totalQuantityOnHand}
            variant="outlined"
            sx={{ mb: 1 }}
          />
        </Grid>
      </Grid>

      <TextField
        fullWidth
        id="itemType"
        name="itemType"
        select
        label="Product Type"
        value={values.itemType}
        onChange={handleChange}
        onBlur={handleBlur}
        error={touched.itemType && Boolean(errors.itemType)}
        helperText={touched.itemType && errors.itemType}
        required
        variant="outlined"
        sx={{ mb: 1 }}
      >
        <MenuItem value={"StockedProduct"}>Stocked Product</MenuItem>
        <MenuItem value={"NonstockedProduct"}>Non Stocked Product</MenuItem>
        <MenuItem value={"Service"}>Service</MenuItem>
      </TextField>

      <FormControlLabel
        control={
          <Checkbox
            checked={values.trackSerials}
            onChange={handleChange}
            name="trackSerials"
            color="primary"
          />
        }
        label="Track Serials"
        sx={{ mb: 1 }}
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={values.isActive}
            onChange={handleChange}
            name="isActive"
            color="primary"
          />
        }
        label="Is Active"
        sx={{ mb: 1 }}
      />

      <Grid container spacing={2}>
        <Grid item xs={6} sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            type="submit"
            variant="contained"
            sx={{ mt: 2, mr: 1 }}
            disabled={isLoading}
          >
            {isLoading ? "Please wait" : "Submit"}
          </Button>
        </Grid>
        <Grid item xs={6}>
          <Button onClick={closeModal} variant="contained" sx={{ mt: 2 }}>
            Cancel
          </Button>
        </Grid>
      </Grid>

      {/* {errorMsg && (
        <Typography variant="body1" color="error">
          {errorMsg}
        </Typography>
      )} */}
    </Box>
  );
};

export default CreateProducts;
